import { translateApiError, useApiClient } from "@/lib/api/api.client";
import PermissionsAlert, {
  PermissionsAlertDescription,
  PermissionsAlertDetails,
  PermissionsAlertTitle,
} from "@/lib/auth/permissions/components/permissions-alert";
import Alert, {
  AlertDescription,
  AlertTitle,
} from "@/lib/components/ui/alert/alert";
import Button from "@/lib/components/ui/button/button";
import LoadingOverlay from "@/lib/components/ui/loading-overlay";
import Skeleton from "@/lib/components/ui/skeleton";
import Text from "@/lib/components/ui/text/text";
import { cn } from "@/lib/utils";
import { useState, type HTMLAttributes, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ThreadsEmpty, {
  ThreadsEmptyDescription,
  ThreadsEmptyTitle,
} from "./components/threads-empty";
import { useConversations } from "./conversations.api";
import ThreadsContext, { useThreadsContext } from "./threads.context";
import { usePinnedThreadIds } from "./threads.pin";
import type { Thread } from "./threads.utils";

export default function Threads({
  children,
  assistantId,
}: {
  children?: ReactNode;
  assistantId: string;
}) {
  const { t } = useTranslation();
  const apiClient = useApiClient();

  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const { data, error, isLoading, refetch } = apiClient.useQuery(
    "get",
    "/assistants/{assistant_id}",
    {
      params: {
        path: {
          assistant_id: assistantId,
        },
      },
    },
  );

  const assistant = data?.data?.assistant;

  if (error) {
    // This is where a group-restricted module lands, so the copy has to be
    // code-aware: translateApiError maps ACCESS_DENIED and the retryable
    // GROUP_ACCESS_UNRESOLVED, where getErrorMessage fell back to "something
    // went wrong" and left the user with nothing to act on.
    return (
      <div className="p-3">
        <Alert variant="critical">
          <AlertTitle>{t("assistant_error_loading_title")}</AlertTitle>
          <AlertDescription>{translateApiError(error)}</AlertDescription>
        </Alert>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => void refetch()}
          className="mt-3"
        >
          {t("common_retry")}
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <>
        <div className="flex flex-col gap-4">
          <header className="h-topbar flex items-center justify-between gap-3 px-3">
            <div className="flex min-w-0 grow items-center gap-3">
              <Skeleton className="h-5 w-20 rounded" />
              <Skeleton className="h-3 w-12 rounded" />
            </div>
            <Skeleton className="size-10 rounded-full" />
          </header>
        </div>
        <ThreadsLoading />
      </>
    );
  }

  if (!assistant) {
    return (
      <div className="p-3">
        <Alert variant="critical">
          <AlertTitle>{t("assistant_error_not_found_title")}</AlertTitle>
          <AlertDescription>
            {t("assistant_error_not_found_description")}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <ThreadsContext.Provider
      value={{
        assistant,
        searchQuery,
        setSearchQuery,
        isSearching,
        setIsSearching,
      }}
    >
      {children}
    </ThreadsContext.Provider>
  );
}

export function ThreadsContent({
  children,
}: {
  children?:
    | ReactNode
    | ((props: {
        threads: Thread[];
        isSearching: boolean;
        pinnedThreads: Thread[];
      }) => ReactNode);
}) {
  const { t } = useTranslation();

  const { assistant, searchQuery, isSearching } = useThreadsContext();
  const pinnedIds = usePinnedThreadIds(assistant.id);

  const { threads, error, isLoading, isRefetching, isEntitled, permissions } =
    useConversations({ assistantId: assistant.id ?? "" });

  if (!isEntitled) {
    return (
      <div className="px-3">
        <PermissionsAlert variant="critical">
          <PermissionsAlertTitle>
            {t("permissions_view_threads_title")}
          </PermissionsAlertTitle>
          <PermissionsAlertDescription>
            {t("permissions_view_threads_description")}
          </PermissionsAlertDescription>
          <PermissionsAlertDetails permissions={permissions} />
        </PermissionsAlert>
      </div>
    );
  }

  if (isLoading) {
    return <ThreadsLoading />;
  }

  if (error) {
    // The conversation list is gated on module access too, so it can carry the
    // same two codes as the module lookup above.
    return (
      <div className="px-3">
        <Alert variant="critical">
          <AlertTitle>{t("threads_error_loading_title")}</AlertTitle>
          <AlertDescription>{translateApiError(error)}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!threads || threads.length === 0) {
    return (
      <ThreadsEmpty>
        <ThreadsEmptyTitle />
        <ThreadsEmptyDescription />
      </ThreadsEmpty>
    );
  }

  // Filter threads based on search query
  const filteredThreads = isSearching
    ? threads.filter((thread) =>
        thread.title?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : threads;

  if (filteredThreads.length === 0) {
    return (
      <ThreadsEmpty>
        <ThreadsEmptyTitle>{t("threads_search_no_results")}</ThreadsEmptyTitle>
      </ThreadsEmpty>
    );
  }

  // Compute pinned and non-pinned threads
  const pinnedThreads = filteredThreads.filter((thread) =>
    pinnedIds.has(thread.id || ""),
  );
  const unpinnedThreads = filteredThreads.filter(
    (thread) => !pinnedIds.has(thread.id || ""),
  );

  return (
    <section className="relative isolate flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-3">
      {typeof children === "function"
        ? children({ threads: unpinnedThreads, pinnedThreads, isSearching })
        : children}
      <LoadingOverlay loading={isRefetching} />
    </section>
  );
}

export function ThreadsLoading({
  className,
  ...props
}: HTMLAttributes<HTMLElement>) {
  return (
    <section className={cn("flex flex-col gap-4 px-3", className)} {...props}>
      <div className="flex flex-col gap-1">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} className="h-10 w-full rounded" />
        ))}
      </div>
    </section>
  );
}

export function ThreadsTitle({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <Text
      variant="label"
      className={cn("min-w-0 truncate text-foreground-pressed", className)}
      {...props}
    >
      {children}
    </Text>
  );
}

export function ThreadsGroup({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("flex flex-col", className)} {...props}>
      {children}
    </div>
  );
}
